import { useTraduccion, type Clave } from '@tallerya/i18n';
import {
  AvisoError,
  Ayuda,
  Boton,
  CampoInterruptor,
  CampoSeleccion,
  CampoTexto,
  useAvisos,
  useFormulario,
  Ventana,
  type EstadoFormulario,
} from '@tallerya/ui';
import { ETAPAS, useCambiosEstados, version, type Estado, type Etapa, type ValoresEstado } from './datos';

// Alta y edición de un estado, en una ventana sobre la pantalla: el mismo patrón
// que «Nueva marca». El nombre, la etapa, el color y —solo en la etapa terminada—
// si congela los montos. El lugar no se edita acá: lo mueven las flechas.

export type Edicion = { tipo: 'nuevo'; etapa: Etapa } | { tipo: 'editar'; estado: Estado } | null;

/** El nombre de cada etapa, para el combo y para los títulos de la pantalla. */
export const NOMBRE_DE_ETAPA: Record<Etapa, Clave> = {
  open: 'admin.estados.etapa.open',
  inProgress: 'admin.estados.etapa.inProgress',
  completed: 'admin.estados.etapa.completed',
  closed: 'admin.estados.etapa.closed',
  cancelled: 'admin.estados.etapa.cancelled',
};

/**
 * Los colores que se ofrecen. Una paleta y no un selector libre: se elige en un
 * toque, no hay `#RRGGBB` que escribir mal, y los puntos de la bandeja se
 * distinguen entre sí. Un color que llegó de otro lado —la API acepta cualquiera—
 * se muestra igual como una muestra más.
 */
const PALETA: readonly { valor: string; nombre: Clave }[] = [
  { valor: '#6B7280', nombre: 'admin.estados.color.gris' },
  { valor: '#2563EB', nombre: 'admin.estados.color.azul' },
  { valor: '#0891B2', nombre: 'admin.estados.color.celeste' },
  { valor: '#16A34A', nombre: 'admin.estados.color.verde' },
  { valor: '#CA8A04', nombre: 'admin.estados.color.amarillo' },
  { valor: '#EA580C', nombre: 'admin.estados.color.naranja' },
  { valor: '#DC2626', nombre: 'admin.estados.color.rojo' },
  { valor: '#7C3AED', nombre: 'admin.estados.color.morado' },
];

interface Props {
  edicion: Edicion;
  onCerrar: () => void;
}

export function VentanaEstado({ edicion, onCerrar }: Props) {
  // La clave reinicia el formulario al abrir otro estado, o al volver a abrir.
  const clave = edicion === null ? 'cerrada' : edicion.tipo === 'nuevo' ? `nuevo-${edicion.etapa}` : String(edicion.estado.id);
  return <Contenido key={clave} edicion={edicion} onCerrar={onCerrar} />;
}

function Contenido({ edicion, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosEstados();
  const estado = edicion?.tipo === 'editar' ? edicion.estado : null;
  const ordenes = Number(estado?.orderCount ?? 0);
  const fija = estado && ordenes > 0 ? estado : null;

  const form = useFormulario<ValoresEstado>({
    inicial: {
      name: estado?.name ?? '',
      category: estado?.category ?? (edicion?.tipo === 'nuevo' ? edicion.etapa : 'inProgress'),
      color: estado?.color ?? '',
      // Uno nuevo de la etapa terminada congela de fábrica, como «Lista» y «Pagada».
      isFinal: estado?.isFinal ?? true,
    },
    guardar: (v) => (estado ? cambios.editar(String(estado.id), version(estado.version), v) : cambios.crear(v)),
    camposPorCodigo: {
      'order_status.nombre_duplicado': 'name',
      'order_status.inicial_solo_abierto': 'category',
      'order_status.ultimo_de_su_etapa': 'category',
      'order_status.con_ordenes': 'category',
    },
  });

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: estado ? t('admin.estados.aviso.editado', { nombre }) : t('admin.estados.aviso.creado', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={estado ? t('admin.estados.ventana.editar') : t('admin.estados.ventana.nuevo')}
      info={estado ? t('admin.estados.ventana.editarTexto') : t('admin.estados.ventana.nuevoTexto')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {estado ? t('admin.estados.guardar') : t('admin.estados.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.estados.campo.nombre')}
        obligatorio
        placeholder={t('admin.estados.campo.ejemplo')}
        autoComplete="off"
        ancho="completo"
      />
      {fija ? (
        // Con órdenes adentro, la etapa y el congelado se muestran, no se eligen: las
        // órdenes los leen del estado, y cambiarlos las movería sin pasar por sus reglas.
        <p className="tipos__dato-fijo">
          {t('admin.estados.campo.etapaFija', { etapa: t(NOMBRE_DE_ETAPA[fija.category]) })}
          {fija.category === 'completed' && fija.isFinal && ` · ${t('admin.estados.congela')}`}{' '}
          <Ayuda
            tema={t('admin.estados.campo.etapa')}
            texto={t('admin.estados.campo.etapaFija.info', { n: ordenes })}
          />
        </p>
      ) : (
        <CampoSeleccion
          form={form}
          nombre="category"
          etiqueta={t('admin.estados.campo.etapa')}
          info={t('admin.estados.campo.etapa.info')}
          opciones={ETAPAS.map((e) => ({ valor: e, etiqueta: t(NOMBRE_DE_ETAPA[e]) }))}
          ancho="completo"
        />
      )}
      <SelectorDeColor form={form} />
      {/* Solo en la terminada lo decide el taller: entregada y anulada siempre congelan, las demás nunca. */}
      {!fija && form.valores.category === 'completed' && (
        <CampoInterruptor
          form={form}
          nombre="isFinal"
          etiqueta={t('admin.estados.campo.congela')}
          info={t('admin.estados.campo.congela.info')}
        />
      )}
    </Ventana>
  );
}

/**
 * Las muestras de la paleta, más «Sin color». Un grupo de radios: el lector de
 * pantalla dice el nombre del color, y las flechas se mueven entre muestras.
 */
function SelectorDeColor({ form }: { form: EstadoFormulario<ValoresEstado> }) {
  const { t } = useTraduccion();
  const actual = form.valores.color.toUpperCase();
  const ajeno = actual !== '' && !PALETA.some((c) => c.valor === actual);
  const muestras = [
    ...PALETA.map((c) => ({ valor: c.valor, nombre: t(c.nombre) })),
    ...(ajeno ? [{ valor: actual, nombre: actual }] : []),
  ];

  return (
    <fieldset className="estados__colores">
      <legend className="ui-campo__etiqueta">{t('admin.estados.campo.color')}</legend>
      <div className="estados__muestras" role="radiogroup" aria-label={t('admin.estados.campo.color')}>
        <label className="estados__muestra estados__muestra--sin" title={t('admin.estados.color.sin')}>
          <input
            type="radio"
            name="color-estado"
            checked={actual === ''}
            onChange={() => form.cambiar('color', '')}
            aria-label={t('admin.estados.color.sin')}
          />
        </label>
        {muestras.map((c) => (
          <label key={c.valor} className="estados__muestra" style={{ background: c.valor }} title={c.nombre}>
            <input
              type="radio"
              name="color-estado"
              checked={actual === c.valor}
              onChange={() => form.cambiar('color', c.valor)}
              aria-label={c.nombre}
            />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
